import React, { ReactNode, createContext, useContext, useMemo, useReducer } from "react";

import { DEFAULT_NORMALIZATION_UNITS } from "constants/chart.constants";
import { FIELD_NAMES } from "constants/fieldLabels.constants";
import { produce } from "immer";

import { IChartScreenshotSettings } from "models/chart";
import { IChartResult } from "models/model";

import { productTabs, tabs } from "../constants/shared.constants";
import { IChartPreset, IMultiphaseChartSettings } from "../models/shared.models";
import {
  ChartReducerActionsT,
  chartSettingsReducer
} from "./reducer/chartSettingsReducer";

export type ScreenshotT = {
  visible: boolean;
  preset: IChartScreenshotSettings | null;
};

export type LegendT = {
  visible: boolean;
  type?: "bottom" | "inplot";
  position?: "NW" | "NE" | "SW" | "SE";
};

export type IAxisMinMax = {
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  /** The user cleared the axis's default product max, so it fits its data. */
  yMaxReset?: boolean;
};

export interface IChartSettingsState {
  id: string;
  bounds: DOMRect | null;
  currentPreset: IChartPreset;
  currentTab: string;
  currentProductTab: string;
  error: string;
  isFetching: boolean;
  isSettingsOpen: boolean;
  isViewMode: boolean;
  isFullscreen: boolean;
  savedPresets: IChartPreset[];
  storedPreset: IChartPreset;
  isMouseOver: boolean;
  isReadOnly: boolean;
  screenshot: ScreenshotT;
  settings: IMultiphaseChartSettings;
  data: IChartResult | undefined;
  legend: LegendT;
  axisMinMax: IAxisMinMax[];
  /**
   * Products hidden by a legend click. Kept out of `settings` and the presets:
   * changing it neither requests the chart again nor edits a preset.
   */
  hiddenSeries?: string[];
  overflowIndex: number;
  /** Toolbar component keys this instance hides, in the strip and the overflow menu. */
  hiddenToolbarKeys?: string[];
  /**
   * Skips the first-preset load. An instance that seeds its own preset would
   * otherwise lose it to whichever preset the user saved first.
   */
  skipInitialPresetLoad?: boolean;
  /**
   * Presets this instance was seeded with that no server holds. They are listed
   * ahead of the saved presets so the user can come back to them, and they are
   * the presets in that list that cannot be edited or deleted.
   */
  builtInPresets?: IChartPreset[];
}

export const initialPresetState: IChartPreset = {
  // TODO: clean up chartType redundancy
  chartType: "monthlyRateCum",
  chartTypeId: 1,
  id: "",
  mBblPosition: "bottom",
  mMcfPosition: "bottom",
  name: undefined,
  ownerId: "",
  products: [],
  rateType: "",
  roleType: "Personal",
  style: []
};

export const initialChartSettingsState: IChartSettingsState = {
  id: "",
  bounds: null,
  currentPreset: initialPresetState,
  currentTab: tabs.chartSettings,
  currentProductTab: productTabs.Production,
  error: undefined,
  isFetching: false,
  isSettingsOpen: false,
  isViewMode: true,
  isFullscreen: false,
  savedPresets: undefined,
  storedPreset: initialPresetState,
  isMouseOver: false,
  isReadOnly: false,
  screenshot: {
    visible: false,
    preset: null
  },
  settings: {
    useNormalizeBy: false,
    normalizeBy: {
      field: "Well_Design.HzLength_m",
      display: FIELD_NAMES.HZ_LENGTH,
      per: 100,
      unit: "",
      defaultUnit: "",
      units: DEFAULT_NORMALIZATION_UNITS,
      useMultilinearNormalization: false,
      threshold: 0,
      lowerScalar: 1,
      higherScalar: 1
    },
    logs: [
      { x: false, y: false },
      { x: false, y: false },
      { x: false, y: false },
      { x: false, y: false }
    ],
    forecast: true,
    producing: false,
    debugMode: true,
    backfit: true
  },
  data: undefined,
  legend: {
    visible: true,
    type: "bottom",
    position: "NE"
  },
  axisMinMax: [
    {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    },
    {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null
    }
  ],
  hiddenSeries: [],
  overflowIndex: undefined,
  hiddenToolbarKeys: [],
  skipInitialPresetLoad: false,
  builtInPresets: undefined
};

export type ChartSettingsDispatchT = (action: ChartReducerActionsT) => void;
export type ChartSettingsProviderT = {
  children: ReactNode;
  initialState?: IChartSettingsState;
};

const curriedReducer = produce(chartSettingsReducer);

const ChartSettingsContext =
  createContext<[IChartSettingsState, React.Dispatch<ChartReducerActionsT>]>(undefined);
ChartSettingsContext.displayName = "ChartSettingsContext";

const ChartSettingsProvider = ({ children, initialState }: ChartSettingsProviderT) => {
  const newState = initialState?.settings ? initialState : initialChartSettingsState;

  const [state, dispatch] = useReducer(curriedReducer, newState);
  const value: [IChartSettingsState, ChartSettingsDispatchT] = useMemo(
    () => [state, dispatch],
    [state]
  );

  return (
    <ChartSettingsContext.Provider value={value}>
      {children}
    </ChartSettingsContext.Provider>
  );
};

const useChartSettings = () => {
  const context = useContext(ChartSettingsContext);
  if (context === undefined) {
    throw new Error(
      "useMultiPhaseChartSettings must be used within ChartSettingsProvider"
    );
  }
  return context;
};

export { curriedReducer, ChartSettingsProvider, useChartSettings };
